'use client'

import { useCallback, useEffect, useRef, useState, useTransition, type RefObject } from 'react'
import { useDebouncedValue } from '@mantine/hooks'
import { useRouter } from 'next/navigation'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { storeRoutes } from '@/lib/routes/store'
import { useGetSearchSuggestionsQuery } from '@/lib/store/api/skuApi'
import { getSearchResultsLoadingQuery, useSearchResultsLoadingQuery } from './searchResultsLoading'
import {
    TYPEAHEAD_DEBOUNCE_MS,
    type SearchTermSuggestion,
    type UseUniversalSearchOptions,
} from './universalSearchTypes'

export interface UseUniversalSearchReturn {
    /** Current search text bound to the input. */
    query: string
    /** Updates `query` as the user types. */
    setQuery: (query: string) => void
    /** Whether the desktop dropdown or mobile panel is open. */
    isOpen: boolean
    /** Closes the panel, dismisses the keyboard, preserves the query, and runs `onClose`. */
    close: () => void
    /** Submits a search and navigates to search results. */
    submit: (searchQuery?: string, method?: string) => void
    /** Submits the selected suggestion's query to full search. */
    selectTermSuggestion: (suggestion: SearchTermSuggestion) => void
    /** Opens the panel on first focus without re-firing analytics on subsequent focuses. */
    handleFocus: () => void
    /** Marshalled suggestions from the SKU suggestions API. */
    searchSuggestions: SearchTermSuggestion[]
    /** True while waiting to search or while results for this text have not arrived. */
    isTypeaheadPending: boolean
    /** Shared input ref for desktop dropdown and mobile dock focus management. */
    inputRef: RefObject<HTMLInputElement | null>
    /** True while a submitted search is loading the results page. */
    isNavigating: boolean
    isInputLoading: boolean
    /** True from an input submit until the panel closes, so the list does not repaint as loading ends. */
    isSuggestionListHidden: boolean
}

/**
 * State and handlers for Universal Search: query text, open/close lifecycle,
 * debounced text suggestions, and navigation to search results.
 *
 * Reopening a recent search shows saved results. Editing the field hides the
 * old list until the new search finishes.
 */
export function useUniversalSearch({
    searchQuery,
    onClose,
    analyticsLocation,
}: UseUniversalSearchOptions = {}): UseUniversalSearchReturn {
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)
    const [isNavigationPending, startNavigation] = useTransition()
    const [submittedQuery, setSubmittedQuery] = useState<string | null>(null)
    const [submittedPanel, setSubmittedPanel] = useState<{
        suggestions: SearchTermSuggestion[]
        isTypeaheadPending: boolean
        method: string
    } | null>(null)
    const resultsLoadingQuery = useSearchResultsLoadingQuery()
    const isNavigating =
        isNavigationPending || (submittedQuery !== null && resultsLoadingQuery === submittedQuery)

    const [query, setQuery] = useState(searchQuery ?? '')
    const [isOpen, setIsOpen] = useState(false)
    const [debouncedQuery] = useDebouncedValue(query, TYPEAHEAD_DEBOUNCE_MS)

    // Fill the field only. Typeahead stays closed until the user focuses.
    useEffect(() => {
        if (searchQuery === undefined) return

        setQuery((currentQuery) => (currentQuery === searchQuery ? currentQuery : searchQuery))
    }, [searchQuery])

    const trimmedQuery = query.trim()
    const trimmedDebouncedQuery = debouncedQuery.trim()

    // Keep the submitted list on screen without refetching while the results page loads.
    const shouldFetchTypeahead = isOpen && submittedPanel === null && trimmedDebouncedQuery.length > 0

    const { currentData: suggestionData, isFetching: isSuggestionFetching } = useGetSearchSuggestionsQuery(
        { query: trimmedDebouncedQuery },
        { skip: !shouldFetchTypeahead },
    )

    const isDebouncePending = trimmedQuery !== trimmedDebouncedQuery

    // Hide the previous list until this text has its own results.
    const liveIsTypeaheadPending =
        trimmedQuery.length > 0 && (isDebouncePending || (isSuggestionFetching && suggestionData === undefined))
    const isTypeaheadPending = submittedPanel?.isTypeaheadPending ?? liveIsTypeaheadPending
    const liveSearchSuggestions =
        shouldFetchTypeahead && !isDebouncePending
            ? (suggestionData ?? []).map((suggestion: string) => ({ label: suggestion, query: suggestion }))
            : []
    const searchSuggestions = submittedPanel?.suggestions ?? liveSearchSuggestions
    const navigatingMethod = isNavigating ? submittedPanel?.method : undefined
    const isInputLoading =
        isNavigating && navigatingMethod !== 'suggestion_click' && navigatingMethod !== 'search_query_click'
    const isSuggestionListHidden =
        submittedPanel !== null &&
        submittedPanel.method !== 'suggestion_click' &&
        submittedPanel.method !== 'search_query_click'

    const open = useCallback(() => {
        setIsOpen(true)
        trackStoreEvent(StoreAnalytics.SEARCH_START, {
            location: analyticsLocation ?? 'universal_search',
        })
    }, [analyticsLocation])

    const close = useCallback(() => {
        setSubmittedQuery(null)
        setSubmittedPanel(null)
        inputRef.current?.blur()
        setIsOpen(false)
        onClose?.()
    }, [onClose])

    const submit = useCallback(
        (searchQuery?: string, method = 'enter_key') => {
            const normalizedQuery = (searchQuery ?? query).trim()
            if (!normalizedQuery || (isNavigating && submittedQuery !== null)) {
                return
            }

            setSubmittedPanel({ suggestions: searchSuggestions, isTypeaheadPending, method })
            inputRef.current?.blur()

            trackStoreEvent(StoreAnalytics.SUBMITTED_SEARCH, {
                query: query.trim(),
                clicked_query: normalizedQuery,
                method,
            })

            setSubmittedQuery(normalizedQuery)
            startNavigation(() => router.push(storeRoutes.search(normalizedQuery)))
        },
        [isNavigating, isTypeaheadPending, query, router, searchSuggestions, submittedQuery],
    )

    useEffect(() => {
        if (isNavigationPending || submittedQuery === null) return
        if (getSearchResultsLoadingQuery() === submittedQuery) return
        setQuery(submittedQuery)
        close()
    }, [close, isNavigationPending, submittedQuery, resultsLoadingQuery])

    const selectTermSuggestion = useCallback(
        (suggestion: SearchTermSuggestion) => {
            submit(suggestion.query, 'suggestion_click')
        },
        [submit],
    )

    const handleFocus = useCallback(() => {
        if (!isOpen) {
            open()
        }
    }, [isOpen, open])

    return {
        query,
        setQuery,
        isOpen,
        close,
        submit,
        selectTermSuggestion,
        handleFocus,
        searchSuggestions,
        isTypeaheadPending,
        inputRef,
        isNavigating,
        isInputLoading,
        isSuggestionListHidden,
    }
}
